// deps
import React, { useCallback, useContext, useState, memo, useMemo } from "react";

// internal-deps
import { isEmpty } from "@swiggy-private/js-utils";
import { Err, Ok, Result } from "@swiggy-private/js-fp";
import { GenericError, useEffectOnce } from "@swiggy-private/core-ui";
import { CheckoutActionTypes, CheckoutContext } from "@swiggy-private/checkout-core";

// interfaces
import { RouteProps } from "@payment-client/interfaces/Router";
import { ApiResponse } from "@payment-client/interfaces/Api";
import { IDineoutCreateOrderResponse, IDineoutOrderStatusResponse } from "@payment-client/interfaces/Order";

// constants
import {
    DINEOUT_ORDER_STATUSES,
    DINEOUT_TERMINAL_ORDER_STATUSES,
} from "@payment-client/constants/DineoutDirectPlaceOrder";

// apis
import { createDineoutOrder, getDineoutOrderStatus } from "@payment-client/apis/DineoutDirectPlaceOrder";
import { Loader } from "@swiggy-private/dls-web-core/dist/components/Loader";
import { LoadingContainer } from "./styles";

function createDineoutOrderWrapper(): Promise<Result<IDineoutCreateOrderResponse["data"], Error | ApiResponse>> {
    return createDineoutOrder()
        .then(orderResponse => {
            return Ok(orderResponse);
        })
        .catch(err => {
            return Err(err);
        });
}

function getDineoutOrderStatusWrapper(
    orderId: string,
): Promise<Result<IDineoutOrderStatusResponse["data"], Error | ApiResponse>> {
    return getDineoutOrderStatus(orderId)
        .then(statusResponse => {
            return Ok(statusResponse);
        })
        .catch(err => {
            return Err(err);
        });
}

const DineoutDirectPlaceOrder = (_props: RouteProps) => {
    const [orderCreationInProgress, setOrderCreationInProgress] = useState(false);
    const { setCheckoutState } = useContext(CheckoutContext);
    const [showError, setShowError] = useState(false);

    const handleErrorCallback = useCallback(() => {
        setShowError(true);
        /* Loading Ends */
        setOrderCreationInProgress(false);
        return;
    }, []);

    const doDirectReservation = useCallback(async () => {
        try {
            /* Loading Starts */
            setOrderCreationInProgress(true);

            const response = await createDineoutOrderWrapper();
            if (response.isErr()) {
                throw response;
            }

            const createOrderResponse = response.unwrap();
            const orderId = createOrderResponse.orders?.[0]?.order_id;
            const placedOrderStatus = createOrderResponse.orders?.[0].order_jobs?.[0]?.status;

            if (isEmpty(orderId) || isEmpty(placedOrderStatus) || placedOrderStatus !== DINEOUT_ORDER_STATUSES.PLACED) {
                handleErrorCallback();
            }

            const orderStatusRawResponse = await getDineoutOrderStatusWrapper(orderId);

            if (orderStatusRawResponse.isErr()) {
                throw orderStatusRawResponse;
            }

            const orderStatusParsedResponse = orderStatusRawResponse.unwrap();
            const confirmedOrderStatus = orderStatusParsedResponse.status?.toUpperCase();

            if (
                isEmpty(confirmedOrderStatus) ||
                !DINEOUT_TERMINAL_ORDER_STATUSES.includes(confirmedOrderStatus as DINEOUT_ORDER_STATUSES)
            ) {
                handleErrorCallback();
            }

            /* Loading Ends */
            setOrderCreationInProgress(false);

            setCheckoutState({
                type: CheckoutActionTypes.DINEOUT_PAYMENT_SUCCESS,
                payload: {
                    orderId,
                    isCheckoutFlow: true, //to differentiate bw standalone success flow and checkout flow
                },
            });
        } catch (e) {
            handleErrorCallback();
        }
    }, [setCheckoutState, handleErrorCallback]);

    useEffectOnce(() => {
        doDirectReservation();
    });

    const goBack = useCallback(() => {
        setCheckoutState({
            type: CheckoutActionTypes.DINEOUT_TABLE_RESERVATION_CART,
        });
    }, [setCheckoutState]);

    const errorActions = useMemo(
        () => ({
            onClick: goBack,
            text: "Go Back",
        }),
        [goBack],
    );

    const renderLoadingState = useCallback(
        () => (
            <LoadingContainer>
                <Loader
                    size="large"
                    type="secondary"
                    foregroundColor="positive"
                    backgroundColor="positive_Sub"
                    isDisabled={false}
                />
            </LoadingContainer>
        ),
        [],
    );

    if (orderCreationInProgress) return renderLoadingState();

    if (showError) {
        return <GenericError action={errorActions} />;
    }

    return null;
};

export default memo(DineoutDirectPlaceOrder);
